import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ModalController, PopoverController, ToastController } from '@ionic/angular';
import { AlertMessager } from 'src/libs/alertMessager';
import { AccountDocument, DocumentType } from 'src/models/account-document.model';
import { Account } from 'src/models/account.model';
import { OrganizationRequiredDocument } from 'src/models/organization-required-document';
import { DocumentService } from 'src/services/document.service';
import { ImageService } from 'src/services/image.service';
import { MediaService } from 'src/services/media.service';
import { UsersService } from 'src/services/users.service';

@Component({
  selector: 'app-secure-document-popover',
  templateUrl: './secure-document-popover.component.html',
  styleUrls: ['./secure-document-popover.component.scss'],
})
export class SecureDocumentPopoverComponent implements OnInit {
  @Input() viewImageDoc: AccountDocument;
  @Input() fileUrl: string;
  @Input() fileName: string;
  @Input() isViewOnly: boolean = false;
  @Input() displayAsModal = true;
  @Input() allowDownload = true;
  @Input() account: Account;

  displayMode: 'file' | 'image';

  viewImageFileName: string;
  previousFileName: string;
  safeUrl: SafeResourceUrl;
  stringUrl: string;
  
  isLoading = false;
  popoverActive = false;
  didFileChange = false;

  docType = DocumentType;
  title = '';

  requiredDoc: OrganizationRequiredDocument;

  hostUrl: string = 'https://nextgenrodeomedia.blob.core.windows.net';

  constructor(
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private alertMessager: AlertMessager,
    private sanitizer: DomSanitizer,
    private userService: UsersService,
    private imageService: ImageService,
    private mediaService: MediaService,
    private documentService: DocumentService) { }

  async ngOnInit() {
    if (this.isViewOnly) {
      this.handlePublicDoc();
    } else {
      this.handlePrivateDoc();
    }
  }

  async handlePublicDoc() {
    this.title = this.fileName;
    this.viewImageFileName = this.fileUrl;

    try {   
      if (this.fileUrl) {

        this.setUrls(this.fileUrl);
  
        if (this.isImageFile()) {
          this.displayMode = 'image';
        } else {
          this.displayMode = 'file';
        }
        console.log(this.displayMode);
      }
    } catch (err) {
      console.log('Error getting document:', err);
      this.alertMessager.showToast(undefined, 'Error getting document: Not a valid url', 'danger');

      if (this.displayAsModal) {
        this.dismiss();
      } else {
        this.dismissPopover();
      }
    }
    
  }

  async handlePrivateDoc() {
    if (this.viewImageDoc.organizationDocumentId) {
      this.requiredDoc = await this.documentService.getRequiredDocument(this.viewImageDoc.organizationDocumentId);
    }
   
    // console.log('viewImageDoc', this.viewImageDoc);
    this.setTitle();
    this.viewImageFileName = this.viewImageDoc.fileName;

    if (!this.viewImageFileName || this.viewImageFileName === '') {
      this.setDocFileName();
      this.openUploadModal(this.viewImageDoc);
    } else {
      try {
        this.isLoading = true;
        this.previousFileName = this.viewImageFileName;
        this.imageService.getPrivateAccountDoc(this.viewImageFileName, this.account.id).then((data) => {
          // console.log(data);
          this.setUrls(data.url);

            if (this.isImageFile()) {
              this.displayMode = 'image';
            } else {
              this.displayMode = 'file';
            }
            // console.log(this.displayMode);
          });
          this.isLoading = false;
        } catch (err) {
          this.isLoading = false;
          console.log('Error getting document:', err);
        }
      }
    }

  downloadFile() {
    const link = document.createElement('a');
    link.href = this.fileUrl;
    link.download = this.displayMode === 'image'
      ? this.fileUrl.split('/images/')[1] : this.fileUrl.split('/files/')[1];
    link.click();
  }

  setDocFileName(): void {
    if (this.viewImageDoc.documentType === this.docType.BirthCertificate) {
      this.viewImageDoc.fileName = this.mediaService.generateFileName(null, 'BirthCertificate');
    } else if (this.viewImageDoc.documentType === this.docType.TaxDocument) {
      this.viewImageDoc.fileName = this.mediaService.generateFileName(null, 'TaxDocument');
    } else {
      this.viewImageDoc.fileName = this.mediaService
        .generateFileName(this.requiredDoc.organization.name, this.requiredDoc.name);
    }

    this.viewImageFileName = this.viewImageDoc.fileName;
    // console.log('viewImageFileName', this.viewImageFileName);
  }

  isImageFile(): boolean {
    return this.viewImageFileName.endsWith('.jpg') ||
      this.viewImageFileName.endsWith('.jpeg') ||
      this.viewImageFileName.endsWith('.png');
  }

  dismiss() {
    this.modalCtrl.dismiss(this.didFileChange);
  }

  dismissPopover() {
    this.popoverCtrl.dismiss(this.didFileChange);
  }

  async openUploadModal(document: AccountDocument) {
    const didUpload = await this.documentService.handleAccountDocUpload(
      document,
      this.account.id,
      this.requiredDoc ? this.requiredDoc.organization.name : undefined,
      this.requiredDoc ? this.requiredDoc.name : undefined);
    
    if (didUpload) {
      this.didFileChange = true;
      this.updateValues();
    }
  }

  setUrls(url: string) {
    if (this.fileUrl && !url.startsWith(this.hostUrl)) {
      url = `https://nextgenrodeomedia.blob.core.windows.net/files/${this.fileUrl}`;
    }

    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.stringUrl = this.sanitizer.sanitize(4, url) as string;

    console.log('string', this.stringUrl);
    console.log('safeUrl', this.safeUrl);
  }

  async updateValues() {
    const updatedAccount = await this.userService.getAccount(this.account.id);

    if (updatedAccount) {
      // Reset view image file name from 'doc' mode
      this.account = updatedAccount;

      const matchingAccountDoc = updatedAccount.documents.find(x => x.fileName.includes(this.viewImageFileName.split('.')[0]));
      // Reset view image file name from 'doc' mode
      if (matchingAccountDoc) {
        this.viewImageDoc = matchingAccountDoc;
        this.viewImageFileName = matchingAccountDoc.fileName;
      }
      // console.log('updated account doc file name', this.viewImageFileName);

      // Update display mode
      if (this.isImageFile()) {
        this.displayMode = 'image';
      } else {
        this.displayMode = 'file';
      }
    }
  }

  private setTitle() {
    if (this.viewImageDoc.documentType === this.docType.BirthCertificate) {
      this.title = 'Birth Certificate';
    } else if (this.viewImageDoc.documentType === this.docType.TaxDocument) {
      this.title = 'TaxDocument';
    } else if (this.viewImageDoc.organizationDocumentId) {
      this.title = this.requiredDoc?.name || 'Unknown';
    }
  } 
}
